import * as React from 'react';
import { Project } from '@shared/types/tsk';
import { Button } from './ui/Button';
import { HardDrive, MapPin, Lock, ChevronRight, Trash2 } from 'lucide-react';

interface ProjectListProps {
  projects: Project[];
  loadProject: (id: string) => void;
  deleteProject: (id: string, e: React.MouseEvent) => void;
  AccountStatus: React.FC;
}

export const ProjectList: React.FC<ProjectListProps> = ({ projects, loadProject, deleteProject, AccountStatus }) => {
  return (
    <section>
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-xs font-black uppercase tracking-widest text-white/40">Recent History</h3>
        <span className="text-[10px] font-bold text-[var(--color-primary)] bg-[var(--color-primary)]/10 px-3 py-1 rounded-full">{projects.length} SKETCHES</span>
      </div>

      {projects.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-20 bg-white/5 rounded-[2.5rem] border border-dashed border-white/10">
          <HardDrive size={32} className="text-white/10 mb-4" />
          <p className="text-white/20 text-[10px] font-bold uppercase tracking-widest">Clean Workspace</p>
        </div>
      ) : (
        <>
          <div className="max-w-6xl mx-auto mb-8">
            <AccountStatus />
          </div>

          <div className="max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 p-4">
            {projects.map(p => (
              <div
                key={p.id}
                onClick={() => loadProject(p.id)}
                data-testid={`project-card-${p.name}`}
                className="group bg-background border border-white/5 rounded-[2rem] overflow-hidden hover:border-primary/40 transition-all cursor-pointer shadow-xl active:scale-[0.98] ring-1 ring-black"
              >
                <div className="h-44 relative bg-neutral-900/50 flex items-center justify-center">
                  <img src={p.thumbnail} alt={p.name} className="w-full h-full object-cover opacity-50 group-hover:opacity-80 transition-opacity" />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a0b] via-transparent to-transparent opacity-80" />

                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={(e) => deleteProject(p.id, e)}
                    className="absolute top-4 right-4 p-2.5 bg-black/50 backdrop-blur-md text-white/40 hover:text-red-400 rounded-xl transition-all opacity-0 group-hover:opacity-100 ring-1 ring-white/10"
                  >
                    <Trash2 size={16} />
                  </Button>

                  {p.address && (
                    <div className="absolute bottom-4 left-4 flex items-center gap-2 text-white/40 font-bold text-[9px] uppercase tracking-wider bg-black/30 backdrop-blur-sm px-2 py-1 rounded-lg">
                      <MapPin size={10} /> <span className="truncate max-w-[150px]">{p.address}</span>
                    </div>
                  )}
                </div>

                <div className="p-5 flex items-center justify-between">
                  <div className="min-w-0">
                    <div className="flex justify-between items-start mb-2">
                      <h3 className="text-sm font-black text-white uppercase tracking-tight truncate flex-1">
                        {p.name}
                      </h3>
                      {p.isLocked && (
                        <div className="ml-2 text-blue-500 bg-blue-500/10 p-1.5 rounded-lg ring-1 ring-blue-500/20 shadow-lg">
                          <Lock size={14} />
                        </div>
                      )}
                      <p className="text-[10px] font-bold text-[#94a3b8] uppercase tracking-widest ml-2 shrink-0">
                        {new Date(p.lastModified).toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                  <div 
                    data-testid={`project-enter-${p.name}`}
                    className="w-9 h-9 shrink-0 rounded-full bg-white/5 flex items-center justify-center text-white/20 group-hover:text-primary group-hover:bg-primary/10 transition-all border border-transparent group-hover:border-primary/20"
                  >
                    <ChevronRight size={18} />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </section>
  );
};
